import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import StarIcon from "@material-ui/icons/Star";
import MessageTemplateApi, {
  AVAILABLE_FIELDS,
  FIELD_CATEGORIES,
  AUDIENCE_LABELS,
  MessageAudience,
  MessageTemplateRes,
  CreateMessageTemplateReq,
} from "api/MessageTemplateApi";

interface FormState {
  messageTemplateId: number | null;
  audience: MessageAudience;
  name: string;
  subject: string;
  body: string;
  enabledFields: string[];
  isDefault: boolean;
}

/** SMS 1 通の文字数上限 (日本語 SMS、 UTF-16 換算で 70 文字) */
const SMS_CHARS_PER_SEGMENT = 70;
/** SMS 1 通あたりの単価 (円) */
const SMS_UNIT_PRICE = 15;

/**
 * プレースホルダごとの想定展開文字数 (見積もり用)。
 * 送信時は server 側で実値置換 → 実コストは renderedBody.length で算出されるが、
 * マスター画面では実値が無いため平均的な値で長さを仮算出する。
 */
const PLACEHOLDER_AVG_LENGTH: Record<string, number> = {
  guestName: 6,
  guestKana: 8,
  guestTel: 13,
  guestCategory: 4,
  guestMemo: 0,
  shopName: 8,
  castName: 5,
  castClassName: 5,
  courseName: 10,
  totalTime: 3,
  orderDate: 16,
  orderPlanInTime: 16,
  orderPlanOutTime: 16,
  orderActualInTime: 16,
  orderActualEndTime: 16,
  cleaningEndTime: 16,
  roomName: 6,
  additionalTime: 2,
  totalFee: 7,
  paymentType: 4,
  optionName: 0,
  discountName: 0,
  areaName: 0,
  hotelName: 0,
  orderAddress: 0,
  orderMemo: 0,
  orderCreatedBy: 0,
  orderUpdatedBy: 0,
};

/** 本文に含まれる {{placeholder}} を平均文字数に仮展開して length を返す */
function estimateSmsLength(body: string): number {
  return body.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    "あ".repeat(PLACEHOLDER_AVG_LENGTH[key] ?? 0)
  ).length;
}

const emptyForm = (audience: MessageAudience): FormState => ({
  messageTemplateId: null,
  audience,
  name: "",
  subject: "",
  body: "",
  enabledFields: AVAILABLE_FIELDS.map((f) => f.key),
  isDefault: false,
});

const MessageTemplate: React.FC = () => {
  const companyId = useSelector(
    (state: { account: { staff: { companyId: number } } }) =>
      state.account.staff.companyId
  );

  const [tab, setTab] = useState<MessageAudience>("customer");
  const [templates, setTemplates] = useState<MessageTemplateRes[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<FormState | null>(null);
  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);

  const fetchTemplates = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const list = await MessageTemplateApi.list(companyId);
      setTemplates(list);
    } catch (e) {
      console.error("テンプレート取得失敗", e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filtered = useMemo(
    () => templates.filter((t) => t.audience === tab),
    [templates, tab]
  );

  const handleNew = () => {
    setEditing(emptyForm(tab));
  };

  const handleEdit = (t: MessageTemplateRes) => {
    setEditing({
      messageTemplateId: t.messageTemplateId,
      audience: t.audience,
      name: t.name,
      subject: t.subject ?? "",
      body: t.body,
      enabledFields: t.enabledFields ?? AVAILABLE_FIELDS.map((f) => f.key),
      isDefault: t.isDefault,
    });
  };

  const handleDelete = async (t: MessageTemplateRes) => {
    if (t.isDefault) {
      window.alert(
        "既定テンプレートは削除できません。 先に他テンプレートを既定に変更してください。"
      );
      return;
    }
    if (
      !window.confirm(
        `テンプレート「${t.name}」を削除します。 よろしいですか?`
      )
    )
      return;
    try {
      await MessageTemplateApi.delete(companyId, t.messageTemplateId);
      fetchTemplates();
    } catch (e) {
      console.error("削除失敗", e);
      window.alert("削除に失敗しました。");
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) {
      window.alert("テンプレート名は必須です。");
      return;
    }
    if (!editing.body.trim()) {
      window.alert("本文は必須です。");
      return;
    }
    try {
      const payload: CreateMessageTemplateReq = {
        audience: editing.audience,
        name: editing.name.trim(),
        subject: editing.subject.trim() || null,
        body: editing.body,
        enabledFields: editing.enabledFields,
        isDefault: editing.isDefault,
      };
      if (editing.messageTemplateId == null) {
        await MessageTemplateApi.create(companyId, payload);
      } else {
        await MessageTemplateApi.update(
          companyId,
          editing.messageTemplateId,
          payload
        );
      }
      setEditing(null);
      fetchTemplates();
    } catch (e) {
      console.error("保存失敗", e);
      window.alert("保存に失敗しました。");
    }
  };

  const insertPlaceholder = (key: string) => {
    if (!editing) return;
    const el = bodyRef.current;
    const token = `{{${key}}}`;
    if (!el) {
      setEditing({ ...editing, body: editing.body + token });
      return;
    }
    const start = el.selectionStart ?? editing.body.length;
    const end = el.selectionEnd ?? editing.body.length;
    const next =
      editing.body.slice(0, start) + token + editing.body.slice(end);
    setEditing({ ...editing, body: next });
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + token.length;
      el.selectionEnd = start + token.length;
    }, 0);
  };

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        メッセージテンプレート
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        受注画面から顧客 / キャスト / スタッフへ送信するメッセージのテンプレートを管理します。
        プレースホルダ <code>{"{{guestName}}"}</code> 等は送信時に Order の情報に置換されます。
      </Typography>

      <Paper style={{ marginTop: 16 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as MessageAudience)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="customer" label="顧客向け" />
          <Tab value="cast" label="キャスト向け" />
          <Tab value="staff" label="スタッフ向け" />
        </Tabs>

        <Box p={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNew}
            disabled={loading}
          >
            新規テンプレート
          </Button>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 40 }} />
              <TableCell>テンプレート名</TableCell>
              <TableCell>件名</TableCell>
              <TableCell>本文プレビュー</TableCell>
              <TableCell style={{ width: 120 }} align="center">
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box py={3}>
                    <Typography variant="body2" color="textSecondary">
                      テンプレートがありません。
                      「新規テンプレート」 から追加してください。
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t) => (
                <TableRow key={t.messageTemplateId} hover>
                  <TableCell>
                    {t.isDefault ? (
                      <Tooltip title="既定">
                        <StarIcon style={{ color: "#fbc02d" }} />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.subject || "—"}</TableCell>
                  <TableCell
                    style={{
                      maxWidth: 360,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.body.slice(0, 60).replace(/\n/g, " ")}
                    {t.body.length > 60 ? "…" : ""}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(t)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 編集ダイアログ - 横並び 2 ペイン */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {editing?.messageTemplateId == null ? "新規" : "編集"} —{" "}
          {editing ? AUDIENCE_LABELS[editing.audience] : ""}
        </DialogTitle>
        <DialogContent dividers>
          {editing && (
            <Box display="flex" style={{ gap: 16, minHeight: 760 }}>
              {/* 左ペイン: 編集 */}
              <Box flex={2} display="flex" flexDirection="column" style={{ gap: 12 }}>
                <TextField
                  label="テンプレート名"
                  value={editing.name}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                  fullWidth
                  required
                  inputProps={{ maxLength: 64 }}
                />
                <TextField
                  label="件名 (メール用、 SMS は無視)"
                  value={editing.subject}
                  onChange={(e) =>
                    setEditing({ ...editing, subject: e.target.value })
                  }
                  fullWidth
                  inputProps={{ maxLength: 255 }}
                />
                <TextField
                  label="本文"
                  value={editing.body}
                  onChange={(e) =>
                    setEditing({ ...editing, body: e.target.value })
                  }
                  fullWidth
                  multiline
                  rows={28}
                  required
                  inputRef={bodyRef}
                />
                {editing.audience === "customer" ? (
                  (() => {
                    const estimatedLength = estimateSmsLength(editing.body);
                    const segments = Math.max(
                      1,
                      Math.ceil(estimatedLength / SMS_CHARS_PER_SEGMENT)
                    );
                    const cost = segments * SMS_UNIT_PRICE;
                    return (
                      <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        style={{
                          gap: 12,
                          padding: "8px 12px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: 4,
                          fontSize: 13,
                        }}
                      >
                        <span>📱 SMS 送信時の見積もり (プレースホルダ展開想定)</span>
                        <span style={{ color: "#555" }}>
                          本文展開後: <strong>{estimatedLength}</strong> 文字
                        </span>
                        <span style={{ color: "#555" }}>
                          送信通数: <strong>{segments}</strong> 通 (70 文字単位)
                        </span>
                        <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                          費用: ¥{cost.toLocaleString()}
                        </span>
                      </Box>
                    );
                  })()
                ) : null}
                <FormControlLabel
                  control={
                    <Switch
                      checked={editing.isDefault}
                      onChange={(e) =>
                        setEditing({ ...editing, isDefault: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="このテンプレートを既定にする (同 audience 内 1 件)"
                />
              </Box>

              {/* 右ペイン: プレースホルダ パネル (固定縦スクロール) */}
              <Box
                flex={1}
                style={{
                  borderLeft: "1px solid #e0e0e0",
                  paddingLeft: 16,
                  overflowY: "auto",
                  maxHeight: 800,
                  minWidth: 280,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  プレースホルダ挿入
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  本文 / 件名のカーソル位置に挿入されます。
                </Typography>
                {FIELD_CATEGORIES.map((cat) => (
                  <Box key={cat.label} mt={2}>
                    <Typography
                      variant="caption"
                      style={{ fontWeight: "bold", color: "#555" }}
                    >
                      {cat.label}
                    </Typography>
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: 4, marginTop: 4 }}
                    >
                      {cat.fields.map((f) => (
                        <Chip
                          key={f.key}
                          label={f.label}
                          size="small"
                          onClick={() => insertPlaceholder(f.key)}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>キャンセル</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageTemplate;
